<template>
  <div
    class="bg-center bg-cover h-auto md:h-[390px] flex flex-col justify-center items-center p-4"
    style="background-image: url(/img/agent-search.jpg)"
  >
    <h1 class="text-3xl font-extrabold text-center text-white md:text-4xl">
      Find your agent to find a home
    </h1>
    <div
      class="md:w-[1121px] w-full p-8 md:mt-14 bg-white rounded-lg shadow-md"
    >
      <div class="flex items-center space-x-2">
        <button
          :class="{
            'bg-primary text-white': activeTab === 'agents',
            'bg-gray-50 border-gray-100 border': activeTab !== 'agents',
          }"
          @click="setActiveTab('agents')"
          class="px-4 py-2 text-sm font-normal text-gray-600 rounded"
        >
          Agents
        </button>
        <button
          :class="{
            'bg-primary text-white': activeTab === 'companies',
            'bg-gray-50 border-gray-100 border': activeTab !== 'companies',
          }"
          @click="setActiveTab('companies')"
          class="px-4 py-2 text-sm font-normal text-gray-600 rounded"
        >
          Companies
        </button>
      </div>

      <div
        class="flex flex-col w-full mx-auto mt-4 space-y-2 md:space-x-4 md:flex-row md:space-y-0"
      >
        <div class="relative">
          <div
            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M15.7399 14.6792L19.7806 18.7197C20.0735 19.0126 20.0735 19.4875 19.7806 19.7804C19.4877 20.0733 19.0128 20.0733 18.7199 19.7804L14.6792 15.7399C13.6632 16.5297 12.3865 17 11 17C7.68629 17 5 14.3137 5 11C5 7.68629 7.68629 5 11 5C14.3137 5 17 7.68629 17 11C17 12.3865 16.5297 13.6632 15.7399 14.6792ZM11 15.5C13.4853 15.5 15.5 13.4853 15.5 11C15.5 8.51472 13.4853 6.5 11 6.5C8.51472 6.5 6.5 8.51472 6.5 11C6.5 13.4853 8.51472 15.5 11 15.5Z"
                fill="currentColor"
              ></path>
            </svg>
          </div>
          <input
            type="text"
            :class="{
              'md:w-[47rem]': activeTab === 'companies',
              'md:w-[318px]': activeTab === 'agents',
            }"
            class="w-full h-[48px] pl-10 px-4 py-2 border-gray-400 placeholder:text-gray-500 placeholder:font-light rounded-md focus:ring-primary focus:border-primary border"
            placeholder="Enter location or agent name"
            v-model="searchTerm"
          />
        </div>

        <div class="md:w-[15rem]">
          <DropDown
            :height="'[48px]'"
            buttonLabel="Service Needed"
            :menuItems="services"
            radius="md"
            unique-identifier="service"
            @menu-item-selected="handleMenuItemSelected"
          />
        </div>
        <div
          v-if="activeTab === 'agents'"
          class="md:w-[30rem] flex md:flex-row flex-col md:space-x-4 md:space-y-0 space-y-2"
        >
          <div class="md:w-[13rem]">
            <DropDown
              :height="'[48px]'"
              buttonLabel="Language"
              :menuItems="languages"
              radius="md"
              unique-identifier="language"
              @menu-item-selected="handleMenuItemSelected"
            />
          </div>
          <div class="md:w-[13rem]">
            <DropDown
              :height="'[48px]'"
              buttonLabel="Nationality"
              :menuItems="countriesDetails"
              itemName="citizenship"
              radius="md"
              unique-identifier="nationality"
              @menu-item-selected="handleMenuItemSelected"
            />
          </div>
        </div>

        <button
          @click="searchAgentsOrCompanies"
          class="flex items-center justify-center md:w-20 h-[48px] px-2 py-1 text-white bg-primary rounded hover:bg-[#910811]"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            stroke="#FFFFFF"
            fill="#FFFFFF"
            class="w-4 h-4"
          >
            <title>Search</title>
            <path
              d="M22 22l-6.344-6.344M10 18a8 8 0 100-16 8 8 0 000 16z"
              stroke="inherit"
              fill="none"
              stroke-width="2"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
  <div class="max-w-6xl px-4 mx-auto md:px-0">
    <template v-if="activeTab === 'agents'">
      <div class="mt-8 mb-8 bg-gray-100 rounded-xl">
        <div class="">
          <div class="flex flex-col p-5 space-y-3">
            <h1 class="font-sans text-3xl font-bold">Find your Agent</h1>
            <p class="py-3 text-stone-700">
              Whether you are looking to buy, sell or rent your home, Hutloop's
              directory of local real estate agents and brokers connects you
              with professionals who can help meet your needs. Because the real
              estate market is unique, it's important to choose a real estate
              agent or broker with local expertise to guide you through the
              process of renting, buying or selling your next home.
            </p>
            <a
              href="/registration-enquiry"
              class="flex items-center justify-center h-10 bg-white border rounded-lg w-28 border-primary text-primary"
            >
              Learn more
            </a>
          </div>
        </div>
      </div>
      <AgentsView :agentsDetails="agentsDetails" :isFullWidth="false" />
    </template>
    <template v-else-if="activeTab === 'companies'">
      <CompaniesView :companiesDetails="companiesDetails" />
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from "vue"; // Import ref for reactive variables

import DropDown from "@/components/form-components/DropDown.vue";
import CompaniesView from "@/features/find-broker/CompaniesView.vue";
import AgentsView from "@/features/find-broker/AgentsView.vue";
import { useGetData } from "@/composables/useGetData";
import { useStore } from "vuex";
import axios from "axios";
export default {
  name: "BannerComponent",
  components: {
    DropDown,
    AgentsView,
    CompaniesView,
  },
  setup() {
    const store = useStore();
    const base_url = store.state.baseUrl;
    const activeTab = ref("agents");
    const isModalOpen = ref(false);
    const nationality = ref("");
    const service = ref("");
    const language = ref("");
    const searchTerm = ref("");
    const languages = ref([
      { value: "english", name: "English" },
      { value: "french", name: "French" },
      { value: "spanish", name: "Spanish" },
      { value: "twi", name: "Twi" },
      { value: "ga", name: "Ga" },
      { value: "ewe", name: "Ewe" },
    ]);
    const services = ref([
      { name: "Residential For Sale", value: "buy" },
      { name: "Residential For Rent", value: "rent" },
      { name: "Commercial For Sale", value: "commercial-buy" },
      { name: "Commercial For Rent", value: "commercial-rent" },
    ]);
    const nationalities = ref([]);

    const apiUrlAgents = `${base_url}agents`;
    const {
      data: agentsDetails,
      error: agentsError,
      fetchData: fetchAgentsData,
    } = useGetData(apiUrlAgents);

    const apiUrlCountries = `${base_url}countries`;
    const {
      data: countriesDetails,
      error: countriesError,
      fetchData: fetchCountriesData,
    } = useGetData(apiUrlCountries);

    const apiUrlCompanies = `${base_url}companies`;
    const {
      data: companiesDetails,
      error: companiesError,
      fetchData: fetchCompaniesData,
    } = useGetData(apiUrlCompanies);

    onMounted(async () => {
      await fetchAgentsData();
      if (window.location.href.includes("#companies")) {
        activeTab.value = "companies";
      } else if (window.location.href.includes("#agents")) {
        activeTab.value = "agents";
      }
    });

    const setActiveTab = async (tab) => {
      if (activeTab.value !== tab) {
        // Only update if the tab is different
        activeTab.value = tab;
        if (tab === "agents" && !agentsDetails.value) {
          await fetchAgentsData();
        } else if (tab === "companies" && !companiesDetails.value) {
          await fetchCompaniesData();
        }
      }
    };

    const openModal = () => {
      isModalOpen.value = true;
    };

    const closeModal = () => {
      isModalOpen.value = false;
    };

    const handleMenuItemSelected = (payload) => {
      if (payload.dropdownIdentifier === "nationality") {
        nationality.value = payload.itemId;
      } else if (payload.dropdownIdentifier === "service") {
        service.value = payload.itemId;
      } else if (payload.dropdownIdentifier === "language") {
        language.value = payload.itemId;
      }
    };

    const searchAgentsOrCompanies = async () => {
      try {
        const response = await axios.get(`${base_url}find-broker`, {
          params: {
            search_term: searchTerm.value,
            type: activeTab.value,
            service_needed: service.value ? service.value : null,
            language: language.value ? language.value : null,
            nationality: nationality.value ? nationality.value : null,
          },
        });
        if (activeTab.value == "agents") {
          agentsDetails.value = response;
        } else if (activeTab.value == "companies") {
          companiesDetails.value = response;
        }
      } catch (error) {
        console.error(error);
      }
    };

    return {
      handleMenuItemSelected,
      searchAgentsOrCompanies,
      language,
      service,
      nationality,
      activeTab,
      isModalOpen,
      languages,
      nationalities,
      agentsDetails,
      companiesDetails,
      setActiveTab,
      openModal,
      closeModal,
      agentsError,
      companiesError,
      countriesDetails,
      countriesError,
      fetchCountriesData,
      services,
      searchTerm,
    };
  },
};
</script>
